import React, { useEffect, useState, useMemo } from 'react';
import Logo from '@/images/logo.svg';
import InputService from '@/utils/input-service';
import ArrowLeft from '@/components/icons/arrow-left';
import { compose, render } from '@/utils/story-composer';
import { ButtonEnum } from '@/enums/styling';
import { getUserCountryBasedOnGeo, getJwtUserId } from '@/utils/cookie-util';
import { getCountryBasedPrice, getCountryBasedPriceLabel, getCountryBasedCurrency } from '@/utils/pricing';

function RequestInvoiceFormGlobal(story) {
	const { pageContext } = story;
	const {
		steps,
		order_summary: orderSummary,
		submit_url: submitUrl,
		license_type: licenseType = 'teacher',
		amount_indentifier: amountIndentifier,
	} = story;

	const [currentStepIndex, setCurrentStepIndex] = useState(0);
	const [isSaving, setIsSaving] = useState(false);
	const [userData, setUserData] = useState({
		user_id: null,
		user_country: null,
		license_type: licenseType,
		lang: pageContext.lang,
	});

	useEffect(() => {
		const userCountry = getUserCountryBasedOnGeo();
		const userId = getJwtUserId();
		setUserData({ ...userData, user_country: userCountry, user_id: userId });
	}, []);

	const currentStep = useMemo(() => steps[currentStepIndex], [currentStepIndex]);
	const headerSteps = steps.filter(s => s.show_in_header === true);

	const submitForm = async () => {
		setIsSaving(true);

		if (submitUrl) {
			const inputService = InputService.getInstance();
			const amount = parseInt(inputService.getInputValue(amountIndentifier), 10) || 1;
			const priceLabel = getCountryBasedPriceLabel(userData.user_country, amount, licenseType, true);
			const currency = getCountryBasedCurrency(userData.user_country);
			const totalPrice = getCountryBasedPrice(userData.user_country, amount, licenseType, true);

			try {
				window?.dataLayer?.push({
					event: 'orderSubmitted',
					licenseType,
					licenseAmount: amount,
					orderTotal: totalPrice,
					currencyCode: currency,
				});
			} catch (e) {
				console.error(e);
				// noop
			}

			const data = {
				...userData,
				...inputService.getAllInputData(),
				order_amount: amount,
				price_label: priceLabel,
			};

			await fetch(submitUrl, {
				method: 'POST',
				body: JSON.stringify(data),
			});
		}
		setIsSaving(false);
	};

	const clickNextStep = async action => {
		if (isSaving) {
			return;
		}

		const inputService = InputService.getInstance();
		if (!inputService.validateInputs()) {
			return;
		}

		switch (action) {
			case 'next_step':
				setCurrentStepIndex(currentStepIndex + 1);
				break;
			case 'submit':
				await submitForm();
				setCurrentStepIndex(currentStepIndex + 1);
				break;
			default:
				break;
		}
	};

	return (
		<div className="w-full min-h-screen h-full bg-sky-lightest">
			<div className="flex justify-between items-center h-20 bg-white px-6 shadow-header">
				<div className="flex-1">
					{currentStep.hide_back_button !== true && currentStepIndex > 0 ? (
						<button
							type="button"
							className={`${ButtonEnum.SECONDARY} w-10 h-10 rounded flex justify-center items-center`}
							onClick={() => setCurrentStepIndex(currentStepIndex - 1)}
						>
							<ArrowLeft />
						</button>
					) : null}
				</div>
				<div className="flex-1">
					<div className="flex justify-center">
						{currentStep.hide_steps_header !== true &&
							headerSteps.map((step, index) => (
								<div key={step._uid} className="flex justify-center">
									<div className="relative h-12">
										<div
											className={`flex justify-center items-center h-7 w-7 rounded-full ${
												currentStepIndex >= index ? 'bg-mariner text-white' : 'bg-solitude'
											}`}
										>
											{index + 1}
										</div>
										<div className="absolute flex justify-center whitespace-nowrap left-[-50%] right-[-50%]">
											{step.name}
										</div>
									</div>
									{index < headerSteps.length - 1 && <div className="w-24 mt-3.5 bg-solitude h-px" />}
								</div>
							))}
					</div>
				</div>
				<div className="flex-1 flex justify-end">
					<Logo />
				</div>
			</div>

			<div className="container py-10 flex">
				<div className="mr-10 w-full prose">
					<div className="relative bg-white rounded-3xl w-full py-8 px-12">
						<div key={currentStep._uid}>
							{render(currentStep.content)}

							{currentStep.next_button_show === true && (
								<div className={`flex pt-10 ${currentStep.next_button_position}`}>
									<button
										type="button"
										className={`${ButtonEnum.PRIMARY} w-52 h-10 rounded`}
										onClick={() => clickNextStep(currentStep.next_button_action)}
									>
										{currentStep.next_button_text}
									</button>
								</div>
							)}
						</div>
					</div>
				</div>

				{currentStep.show_order_summary && (
					<div className="min-w-[35%]">
						<div className="flex bg-white rounded-3xl">{render(orderSummary)}</div>
					</div>
				)}
			</div>
		</div>
	);
}

export default RequestInvoiceFormGlobal;
compose('request-invoice-form-global', RequestInvoiceFormGlobal);
